import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { api, ApiError } from "../api/client";
import type { PriceDeal, PriceOption, PriceProduct, PriceRole, PriceRow, PriceSG, PriceSGDeal, PriceSGSeries, PriceSheet } from "../api/types";
import {
  AGENT_TIERS,
  ago,
  copy,
  copyText,
  addonName,
  daysLeft,
  dealBits,
  dealsFor,
  hasDeal,
  haystack,
  headline,
  isDealer,
  loadPrices,
  matesOf,
  md,
  rm,
  ROLE_NAME,
  rowsFor,
  seriesOf,
  sgCopyText,
  sgd,
  sgDay,
  sgDaysLeft,
  sgHeadline,
  sgLive,
  sgOf,
  sgRows,
  storeLastDay,
  usePrices,
  type Market,
  type Platform,
  type SGRow,
} from "../prices";
import { useBackLayer } from "../backstack";
import { Btn, Card, Empty, IconBox, PageHead, Pill, SectionHead, Seg, SegInd, usePlay } from "./Kit";
import { Ring, Sheet, toast } from "./AdminKit";
import { ShellIcon, type ShellIconName } from "./ShellIcons";
import "../prices.css";
import PageSkel from "./PageSkel";

/*
 * 价目表 (owner, 2026-10-04): the page, and the same answer in a chat's 价钱
 * drawer. A colleague picks who is asking — 客户, VIP, a dealer tier — and a
 * product, and reads both shops' prices for exactly that person, then copies
 * them into the chat. One line per product, as customers ask for one
 * ("KIWINET 多少钱"); its prices are its series', and the page says what it
 * may be mixed with.
 *
 * Redone as prototype v2.20's shop window (10/23): the product large, a line
 * that says what it costs for the person asking and which size is the best
 * buy, the table under it, and the words to paste as the WhatsApp bubble the
 * customer will get. Everything the page did before is still here.
 */

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

const ROLE_KEY = "lazior_price_role";

function savedRole(): PriceRole {
  try {
    const r = localStorage.getItem(ROLE_KEY);
    if (r && r in ROLE_NAME) return r as PriceRole;
  } catch {
    /* private window: start from 客户 */
  }
  return "customer";
}

/** Who is asking, remembered on this device: a colleague answering dealers all day keeps 代理. */
function useRole(): [PriceRole, (r: PriceRole) => void] {
  const [role, setRole] = useState<PriceRole>(savedRole);
  const pick = (r: PriceRole) => {
    setRole(r);
    try {
      localStorage.setItem(ROLE_KEY, r);
    } catch {
      /* not remembered, still picked */
    }
  };
  return [role, pick];
}

/** 客户 · VIP · 代理▾ · 经销商, the kit's choice with 代理's four tiers behind ▾. */
function RoleSeg({ role, onRole }: { role: PriceRole; onRole: (r: PriceRole) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const agent = AGENT_TIERS.includes(role);
  const btn = (r: PriceRole, label: string) => (
    <button type="button" role="radio" aria-checked={role === r} className={role === r ? "on" : ""} onClick={() => onRole(r)}>
      {label}
    </button>
  );
  return (
    <div className="pl-who" ref={box}>
      <div className="lz-seg md" role="radiogroup" aria-label="谁在问">
        <SegInd />
        {btn("customer", "客户")}
        {btn("vip", "VIP")}
        <button type="button" className={agent ? "on" : ""} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {agent ? ROLE_NAME[role] : "代理"}
          <ShellIcon name="down" size={14} />
        </button>
        {btn("distributor", "经销商")}
      </div>
      {open && (
        <div className="pl-menu" role="menu">
          {AGENT_TIERS.map((r) => (
            <button
              key={r}
              type="button"
              role="menuitemradio"
              aria-checked={r === role}
              className={r === role ? "on" : ""}
              onClick={() => {
                onRole(r);
                setOpen(false);
              }}
            >
              <span>{ROLE_NAME[r]}</span>
              {r === role && <ShellIcon name="check" size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- sums ---- */

const tagText = (o: PriceOption) =>
  o.tag === "deal" ? o.label || "活动" : o.tag === "set" ? "配套" : o.tag === "addon" ? "加购" : "";

/** One of n units, rounded to the sen: 「每盒 RM87.50」. */
const perRM = (sen: number, n: number) => rm(Math.round(sen / n));
const perSGD = (cents: number, n: number) => sgd(Math.round(cents / n));

/** The size whose unit price is lowest, when it beats buying the fewest: 「6 盒最划算」. */
function bestOf(cells: Array<{ n: number; sen?: number }>): number | null {
  const per = cells.filter((c): c is { n: number; sen: number } => c.sen != null).map((c) => ({ n: c.n, per: c.sen / c.n }));
  if (per.length < 2) return null;
  const min = Math.min(...per.map((c) => c.per));
  const base = per.find((c) => c.n === 1) ?? per[0]!;
  return min < base.per - 0.5 ? per.find((c) => c.per === min)!.n : null;
}
const bestIn = (rows: PriceRow[], col: Platform) => bestOf(rows.map((r) => ({ n: r.n, sen: r[col][0]?.sen })));

/**
 * The site a product's prices are read from for this person: the one picked by the copy button, unless the product is
 * on one site only or a dealer is asking (their ladder is shop.lazior.com's). The line under the name and the copy
 * button read the same one: the line said 「6 盒最划算」 from store while the copy button sent shop's 3-box price.
 */
const platformOf = (p: PriceProduct, role: PriceRole, picked: Platform): Platform =>
  !p.on_store ? "shop" : !p.on_shop ? "store" : isDealer(role) && !p.addon ? "shop" : picked;

/** The shop.lazior.com group a product is listed under: 「Fibre Dietary Detox」. */
const groupOf = (sheet: PriceSheet, p: PriceProduct) => sheet.groups.find((g) => g.products.includes(p.key))?.name ?? "";

/** When this person's promotions on this product end, the soonest: a shop deal's, else the storefront's. */
function dealEnds(p: PriceProduct, role: PriceRole): string {
  const shop = dealsFor(p, role)
    .filter((d) => d.live && d.ends)
    .map((d) => d.ends!)
    .sort((a, b) => Date.parse(a) - Date.parse(b))[0];
  if (shop) return md(shop);
  const c = isDealer(role) ? undefined : p.store?.campaigns?.[0];
  return c ? storeLastDay(c.ends) : "";
}

/**
 * The page's one line: when the first running promotion ends (a customer's view), and whether they all end then. It
 * said when most of them end — 「活动到 12/31」 over cards saying 到 10/31 (QA Q-03) — and a colleague quoting it would
 * have given a customer the wrong last day.
 */
function sheetEnds(sheet: PriceSheet): { day: string; left: number; all: boolean } | null {
  const ends: Array<{ day: string; at: number }> = [];
  for (const p of sheet.products) {
    for (const d of dealsFor(p, "customer")) if (d.live && d.ends) ends.push({ day: md(d.ends), at: Date.parse(d.ends) });
    for (const c of p.store?.campaigns ?? []) ends.push({ day: storeLastDay(c.ends), at: Date.parse(c.ends) - 1000 });
  }
  const first = ends.filter((e) => Number.isFinite(e.at)).sort((a, b) => a.at - b.at)[0];
  if (!first) return null;
  return { day: first.day, left: Math.max(0, daysLeft(new Date(first.at).toISOString())), all: ends.every((e) => e.day === first.day) };
}

const hhmm = () => new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kuala_Lumpur", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date());

/* ---- the table ---- */

/** A row's turn coming in (prices.css pl-row): the table's 40 ms apart, the ladder's 35, as the prototype's. */
const rowDelay = (i: number, step: number) => ({ "--d": `${(i * step).toFixed(3)}s` }) as CSSProperties;

function Tag({ o }: { o: PriceOption }) {
  const w = tagText(o);
  return w ? <span className={cx("pl-tag", o.tag === "deal" ? "hot" : o.tag)}>{w}</span> : null;
}

function Cell({
  opts,
  n,
  unit,
  best,
  unset,
  none,
  onPick,
}: {
  opts: PriceOption[];
  n: number;
  unit: string;
  best?: boolean;
  unset?: boolean;
  none: string;
  /** In the chat's drawer: the price, as a line, into the reply box. */
  onPick?: () => void;
}) {
  if (unset)
    return (
      <div className="pl-cell none">
        <span>没设定</span>
      </div>
    );
  const c = opts[0];
  if (!c)
    return (
      <div className="pl-cell none">
        <span>{none}</span>
      </div>
    );
  const Box = onPick ? "button" : "div";
  return (
    <Box type={onPick ? "button" : undefined} className={cx("pl-cell", best && "best", onPick && "pick")} title={onPick ? "放进输入框" : undefined} onClick={onPick}>
      {onPick && <ShellIcon name="plus" size={14} className="pl-pd-add" />}
      <div className="pl-p">
        <b className="lz-n">{rm(c.sen)}</b>
        <Tag o={c} />
      </div>
      {n > 1 && (
        <div className="pl-per lz-n">
          每{unit} {perRM(c.sen, n)}
          {best && (
            <span className="pl-best">
              <ShellIcon name="star" size={11} />
              最划算
            </span>
          )}
        </div>
      )}
      {opts.slice(1).map((o, i) => (
        <div key={i} className="pl-alt lz-n">
          或 {rm(o.sen)}
          {tagText(o) ? ` · ${tagText(o)}` : ""}
        </div>
      ))}
    </Box>
  );
}

/** A size's price as one line for the reply box: 「ZENITE 3 盒 RM393（活动）」. */
const priceLine = (p: PriceProduct, n: number, o: PriceOption) => `${p.name} ${n} ${p.unit} ${rm(o.sen)}${tagText(o) ? `（${tagText(o)}）` : ""}`;

function CompareTable({ p, role, onPick }: { p: PriceProduct; role: PriceRole; onPick?: (line: string) => void }) {
  const rows = rowsFor(p, role);
  const bestShop = bestIn(rows, "shop");
  // The storefront has no VIP or dealer price: its best buy is pointed out to a customer only.
  const bestStore = role === "customer" ? bestIn(rows, "store") : null;
  const who = role === "vip" ? "VIP 客户" : isDealer(role) ? "所有人" : "客户";
  return (
    <div className="pl-tbl">
      <div className="pl-th">
        <span>数量</span>
        <span className="pl-site shop">
          <b>shop.lazior.com</b>
          <small>{who}</small>
        </span>
        <span className="pl-site store">
          <b>store.lazior.com</b>
          <small>{role === "vip" ? "没有 VIP 价，跟客户一样" : "网站 · 小爱 · cp 开单"}</small>
        </span>
      </div>
      {rows.map((r, i) => (
        <div className="pl-tr" key={r.n} style={rowDelay(i, 0.04)}>
          <span className="pl-q">
            <b className="lz-n">{r.n}</b> {p.unit}
          </span>
          <Cell
            opts={r.shop}
            n={r.n}
            unit={p.unit}
            best={bestShop === r.n}
            unset={r.unset}
            none={p.on_shop ? "没有" : "没卖"}
            onPick={onPick && r.shop[0] ? () => onPick(priceLine(p, r.n, r.shop[0]!)) : undefined}
          />
          <Cell
            opts={r.store}
            n={r.n}
            unit={p.unit}
            best={bestStore === r.n}
            none={p.on_store ? "没有" : "没卖"}
            onPick={onPick && r.store[0] ? () => onPick(priceLine(p, r.n, r.store[0]!)) : undefined}
          />
        </div>
      ))}
      {rows.length === 0 && <div className="pl-na">两边都还没有价钱</div>}
    </div>
  );
}

/** 累积销售额: a dealer's unit price at each step of their total sales, and how far under where they start. */
function Ladder({ sheet, p, role }: { sheet: PriceSheet; p: PriceProduct; role: PriceRole }) {
  const lad = (p.on_shop && seriesOf(sheet, p).ladders?.[role]) || [];
  if (!lad.length)
    return (
      <div className="pl-na">
        <ShellIcon name="info" size={16} />
        {p.on_shop ? `shop.lazior.com 没有${ROLE_NAME[role]}价，只卖活动` : "shop.lazior.com 没卖"}
      </div>
    );
  const top = lad[0]!.unit;
  const low = Math.min(...lad.map((r) => r.unit));
  const customer = rowsFor(p, "customer").find((r) => r.n === 1)?.shop[0]?.sen;
  return (
    <div className="pl-lad" role="table" aria-label="累积销售额">
      <div className="pl-lad-h" role="row">
        <span>累积销售额</span>
        <span>每{p.unit}拿货价</span>
        <span>比一开始少</span>
      </div>
      {lad.map((r, i) => {
        const off = top ? (top - r.unit) / top : 0;
        return (
          <div key={`${r.from}-${r.cart ?? 0}`} className={cx("pl-lad-r", r.unit === low && low < top && "low")} role="row" style={rowDelay(i, 0.035)}>
            <span className="pl-lad-k">
              <b className="lz-n">{r.from <= 1 ? "一开始" : `${rm(r.from)} 起`}</b>
              {r.cart ? <small className="lz-n">每单满 {rm(r.cart)}</small> : null}
            </span>
            <span className="pl-lad-v">
              <b className="lz-n">{rm(r.unit)}</b>
            </span>
            <span className="pl-lad-o">
              {off > 0 ? <span className="pl-off lz-n">−{Math.round(off * 100)}%</span> : r.unit === customer ? <span className="pl-small">跟客户一样</span> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** A deal's parts, each kept on one line. */
function DealBits({ d, unit }: { d: PriceDeal; unit: string }) {
  return (
    <>
      {dealBits(d, unit).map((b, i) => (
        <span key={i}>
          {i ? " ＋ " : ""}
          <span className="pl-nw">{b}</span>
        </span>
      ))}
    </>
  );
}

function Notes({ sheet, p, role }: { sheet: PriceSheet; p: PriceProduct; role: PriceRole }) {
  const mates = matesOf(sheet, p).map((x) => x.name);
  const notes: Array<[ShellIconName, string, boolean?]> = [];
  if (p.addon) notes.push(["plus", "只能跟产品一起买，不能单买"]);
  if (!p.on_store) notes.push(["lock", "store.lazior.com 没卖"]);
  else if (isDealer(role) && !p.addon) notes.push(["lock", "store.lazior.com 只卖客户"]);
  else if (!p.available) notes.push(["alert", "store.lazior.com 现在缺货", true]);
  if (!p.on_shop) notes.push(["lock", "shop.lazior.com 没卖"]);
  if (mates.length) notes.push(["layers", `可以跟 ${mates.join("、")} 混搭（配套、活动都算）`]);
  if (p.alias) notes.push(["tag", `shop.lazior.com 上叫 ${p.alias}`]);
  const addons = p.addon ? [] : (p.addons ?? []);
  if (addons.length) notes.push(["plus", `shop.lazior.com 可加购 ${addons.map((a) => `${addonName(a)} ${rm(a.sen)}`).join("、")}`]);
  if (!notes.length) return null;
  return (
    <ul className="pl-notes">
      {notes.map(([icon, n, bad]) => (
        <li key={n} className={bad ? "bad" : undefined}>
          <ShellIcon name={icon} size={15} />
          {n}
        </li>
      ))}
    </ul>
  );
}

/* ---- the words to paste, as the bubble the customer gets ---- */

/** What the customer will get, as the WhatsApp bubble it will be: whole, on a phone too (prototype prices.js 443). */
function Bubble({ text, note }: { text: string; note: string }) {
  return (
    <>
      <div className="pl-wall">
        <div className="pl-bubble" key={text}>
          <div className="pl-btext">{text}</div>
          <div className="pl-bmeta lz-n">
            {hhmm()}
            <ShellIcon name="check2" size={15} />
          </div>
        </div>
      </div>
      <div className="pl-copy-n">{note}</div>
    </>
  );
}

/** A dealer's prices in a few lines: where the ladder starts, its first step down, its lowest, and the running deals.
 *  The whole ladder (ten steps and more, with the deals) was a WhatsApp wall nobody read; it is the second button.
 *  null when the ladder is that short anyway. */
function shortLadder(sheet: PriceSheet, p: PriceProduct, role: PriceRole): string | null {
  const steps = (p.on_shop && !p.addon && isDealer(role) && seriesOf(sheet, p).ladders?.[role]?.length) || 0;
  if (steps <= 3) return null;
  // copyText's lines: its title, a line a step, then the deals.
  const lines = copyText(sheet, p, role, "shop").split("\n");
  return [lines[0], lines[1], lines[2], `……（中间还有 ${steps - 3} 级）`, lines[steps], ...lines.slice(steps + 1)].join("\n");
}

/** The copy button and what it copies: 已复制 for a moment once it is on the clipboard. */
function useCopied(text: string): [boolean, () => void] {
  const [done, setDone] = useState(false);
  useEffect(() => setDone(false), [text]);
  const go = () =>
    void copy(text).then((ok) => {
      setDone(ok);
      if (ok) {
        toast("已复制，去 WhatsApp 贴上", "win");
        window.setTimeout(() => setDone(false), 1800);
        // A browser that refuses the clipboard refuses it again: no point asking for another press (prototype prices.js 199).
      } else toast("复制不了，请长按文字自己复制");
    });
  return [done, go];
}

function CopyCard({
  sheet,
  p,
  role,
  platform,
  onPick,
}: {
  sheet: PriceSheet;
  p: PriceProduct;
  role: PriceRole;
  platform: Platform;
  onPick: (pl: Platform) => void;
}) {
  const dealer = isDealer(role);
  const both = p.on_store && p.on_shop;
  const full = useMemo(() => copyText(sheet, p, role, platform), [sheet, p, role, platform]);
  // A dealer's long ladder: the main button copies it short, the whole one is the quieter button beside it.
  const short = useMemo(() => shortLadder(sheet, p, role), [sheet, p, role]);
  const text = short ?? full;
  const [done, go] = useCopied(text);
  const [doneAll, goAll] = useCopied(full);
  // 经销商 is a dealer too, but not an 代理: the button said 「复制给代理」 with 经销商 picked.
  const who = role === "distributor" ? "经销商" : dealer ? "代理" : role === "vip" ? " VIP" : ROLE_NAME[role];
  const choose = both && (!dealer || p.addon);
  return (
    <Card className={cx("pl-copy lz-s5", short && "two")}>
      <SectionHead
        title={`复制给${who}`}
        right={
          choose ? (
            <Seg
              size="sm"
              className="pl-plat"
              value={platform}
              onChange={onPick}
              options={[
                ["store", "store"],
                ["shop", "shop"],
              ]}
            />
          ) : short ? (
            <Btn kind="ghost" size="sm" icon={doneAll ? "check" : "copy"} onClick={goAll}>
              {doneAll ? "已复制" : "复制整张阶梯"}
            </Btn>
          ) : undefined
        }
      />
      <Btn kind={done ? "soft" : "primary"} size="lg" icon={done ? "check" : "copy"} onClick={go}>
        {done ? "已复制" : `复制给${who}`}
      </Btn>
      <Bubble text={text} note={dealer && !p.addon ? `${ROLE_NAME[role]} 收到的就是这段` : `收到的就是这段 · ${platform}.lazior.com 的价钱`} />
    </Card>
  );
}

/* ---- promotions ---- */

/** Days from a promotion's start to its end, for its ring; a month when the start is not known. */
const spanDays = (d: PriceDeal) => (d.starts && d.ends ? Math.max(1, Math.round((Date.parse(d.ends) - Date.parse(d.starts)) / 864e5)) : 31);

function Promos({ p, role }: { p: PriceProduct; role: PriceRole }) {
  const dealer = isDealer(role);
  const deals = dealsFor(p, role);
  const campaigns = dealer ? [] : (p.store?.campaigns ?? []);
  if (p.addon && !deals.length && !campaigns.length) return null;
  const live = deals.filter((d) => d.live && d.ends);
  const sameEnd = live.length > 0 && live.every((d) => md(d.ends!) === md(live[0]!.ends!));
  const left = sameEnd ? Math.max(0, daysLeft(live[0]!.ends!)) : 0;
  return (
    <Card className="pl-promo lz-s12">
      <div className="pl-promo-h">
        <h3>
          <i className="pl-sdot shop" />
          shop.lazior.com 活动
        </h3>
        {sameEnd && (
          <span className="pl-count">
            <Ring value={left / spanDays(live[0]!)} size={30} stroke={4} />
            <span>
              到 <b className="lz-n">{md(live[0]!.ends!)}</b> · 还有 <b className="lz-n">{left}</b> 天
            </span>
          </span>
        )}
      </div>
      {deals.length ? (
        <div className="pl-deals">
          {deals.map((d) => {
            const only = d.roles?.length ? d.roles.map((r) => ROLE_NAME[r]).join("、") : "";
            return (
              <div key={d.id} className={cx("pl-deal", !d.live && "later")}>
                <div className="pl-deal-t">
                  <b className={d.label ? undefined : "long"}>{d.label || d.name}</b>
                  <span className="lz-n">{rm(d.sen)}</span>
                  <small>
                    {d.units} {p.unit}
                  </small>
                </div>
                <div className="pl-deal-how lz-n">
                  <DealBits d={d} unit={p.unit} />
                  {dealer && d.profit ? (
                    <>
                      {" · "}
                      <b className="win">
                        加购的每{p.unit}赚 {rm(d.profit)}
                      </b>
                    </>
                  ) : null}
                </div>
                <div className="pl-deal-tags">
                  {only && !dealer && <Pill icon="lock">只限 {only}</Pill>}
                  {!d.live && d.starts && (
                    <Pill tone="warn" icon="calendar">
                      {md(d.starts)} 开始
                    </Pill>
                  )}
                  {d.live && d.ends && !sameEnd && <Pill icon="clock">到 {md(d.ends)}</Pill>}
                  {/* each add-on whole on its line: 「其他」 and 「加购品 RM6.90」 broke apart (QA R-04) */}
                  {d.extras?.length ? (
                    <span className="pl-small">
                      可加购{" "}
                      {d.extras.map((x, i) => (
                        <span key={i}>
                          {i ? "、" : ""}
                          <span className="pl-nw">{`${addonName(x)} ${rm(x.sen)}`}</span>
                        </span>
                      ))}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="pl-nopromo">
          <IconBox icon="tag" />
          <div>
            <b>现在没有活动</b>
            <div className="pl-small">{dealer ? `${ROLE_NAME[role]} 照累积销售额的拿货价报` : "照上面的价钱报"}</div>
          </div>
        </div>
      )}
      {campaigns.length > 0 && (
        <>
          <div className="pl-promo-h store">
            <h3>
              <i className="pl-sdot store" />
              store.lazior.com 活动
            </h3>
          </div>
          <div className="pl-others">
            {campaigns.map((c) => (
              <div key={c.name} className="pl-other">
                <IconBox icon="gift" tone="warn" />
                <div>
                  <b>{c.name}</b>
                  <div className="pl-small">到 {storeLastDay(c.ends)} · 表里标「活动」的就是</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

/** Where the numbers come from, and whether they are fresh. */
function Sources({ sheet }: { sheet: PriceSheet }) {
  const s = sheet.shop;
  const stale = !!s.synced_at && Date.now() - Date.parse(s.synced_at) > 2 * 60 * 60 * 1000;
  const warn = !s.ok || !!s.failing || stale;
  const shopWord = !s.ok
    ? "活动读不到，只显示固定价"
    : s.failing || stale
      ? `活动资料${s.synced_at ? ` ${ago(s.synced_at)}` : ""}同步失败，可能不是最新`
      : s.synced_at
        ? `活动 ${ago(s.synced_at)}自动同步`
        : "活动已读取";
  return (
    <div className="pl-foot">
      <span className={warn ? "warn" : undefined}>
        <i className={cx("pl-live", warn && "warn")} />
        shop.lazior.com · {shopWord}
      </span>
      <span className={sheet.store.ok ? undefined : "warn"}>
        <i className={cx("pl-sdot", sheet.store.ok ? "store" : "warn")} />
        store.lazior.com · {sheet.store.ok ? "跟结账同一个价" : "读不到"}
      </span>
    </div>
  );
}

/* ---- 新加坡 ---- */

/** 马来西亚 · 新加坡: which list the whole page reads. */
function MarketSeg({ market, onMarket, size }: { market: Market; onMarket: (m: Market) => void; size?: "sm" }) {
  return (
    <Seg
      size={size}
      className="pl-mkt"
      value={market}
      onChange={onMarket}
      options={[
        [
          "my",
          <>
            马来西亚 <span className="pl-cur">RM</span>
          </>,
        ],
        [
          "sg",
          <>
            新加坡 <span className="pl-cur">SGD</span>
          </>,
        ],
      ]}
    />
  );
}

/** A Singapore size's first offer: its price, else its first promotion. */
const sgFirst = (r: SGRow) => (r.offer ? r.offer.cents : r.deals[0]?.cents);

function SGCell({ r, unit, best }: { r: SGRow; unit: string; best: boolean }) {
  const o = r.offer;
  const lead: { cents: number; was?: number; deal?: PriceSGDeal } | null = o ? { cents: o.cents, was: o.was } : r.deals[0] ? { cents: r.deals[0].cents, was: r.deals[0].was, deal: r.deals[0] } : null;
  if (!lead) return null;
  const rest = o ? r.deals : r.deals.slice(1);
  return (
    <div className={cx("pl-cell", best && "best")}>
      <div className="pl-p">
        <b className="lz-n">{sgd(lead.cents)}</b>
        {lead.deal && (
          <span className={cx("pl-tag hot", lead.deal.state === "soon" && "soon")}>
            {lead.deal.label}
            {lead.deal.state === "soon" && lead.deal.starts ? ` · ${sgDay(lead.deal.starts)} 开始` : ""}
          </span>
        )}
      </div>
      {lead.was ? (
        <div className="pl-was lz-n">
          原价 <s>{sgd(lead.was)}</s>
        </div>
      ) : null}
      {r.n > 1 && (
        <div className="pl-per lz-n">
          每{unit} {perSGD(lead.cents, r.n)}
          {best && (
            <span className="pl-best">
              <ShellIcon name="star" size={11} />
              最划算
            </span>
          )}
        </div>
      )}
      {rest.map((d, i) => (
        <div key={i} className="pl-alt lz-n">
          {o ? "或 " : ""}
          {sgd(d.cents)} · {d.label}
          {d.state === "soon" && d.starts ? `（${sgDay(d.starts)} 开始）` : ""}
        </div>
      ))}
    </div>
  );
}

function SGTable({ p, s }: { p: PriceProduct; s: PriceSGSeries }) {
  const rows = sgRows(s);
  const best = bestOf(rows.map((r) => ({ n: r.n, sen: sgFirst(r) })));
  return (
    <div className="pl-tbl one">
      <div className="pl-th">
        <span>数量</span>
        <span className="pl-site sg">
          <b>新加坡 · 新币 SGD</b>
          <small>包邮</small>
        </span>
      </div>
      {rows.map((r, i) => (
        <div className="pl-tr" key={r.n} style={rowDelay(i, 0.04)}>
          <span className="pl-q">
            <b className="lz-n">{r.n}</b> {p.unit}
          </span>
          <SGCell r={r} unit={p.unit} best={best === r.n} />
        </div>
      ))}
      {rows.length === 0 && <div className="pl-na">新加坡还没有价钱</div>}
    </div>
  );
}

function SGNotes({ sheet, p, s }: { sheet: PriceSheet; p: PriceProduct; s?: PriceSGSeries }) {
  const mates = matesOf(sheet, p).map((x) => x.name);
  return (
    <ul className="pl-notes">
      {s && mates.length > 0 && (
        <li>
          <ShellIcon name="layers" size={15} />
          可以跟 {mates.join("、")} 混搭
        </li>
      )}
      {s && (
        <li>
          <ShellIcon name="calc" size={15} />
          表上没有的件数（例如 4 件以上），请同事算好再报
        </li>
      )}
      <li>
        <ShellIcon name="sparkles" size={15} />
        +65 号码的客户，小爱照这张报新币；资料收齐交给同事下单
      </li>
    </ul>
  );
}

function SGPromos({ p, s }: { p: PriceProduct; s?: PriceSGSeries }) {
  const deals = (s?.deals ?? []).filter((d) => d.state !== "ended");
  return (
    <Card className="pl-promo lz-s12">
      <div className="pl-promo-h">
        <h3>
          <i className="pl-sdot sg" />
          新加坡活动
        </h3>
      </div>
      {deals.length ? (
        <div className="pl-deals">
          {deals.map((d, i) => (
            <div key={i} className={cx("pl-deal", d.state === "soon" && "later")}>
              <div className="pl-deal-t">
                <b className="long">{d.label}</b>
                <span className="lz-n">{sgd(d.cents)}</span>
                {d.was ? <s className="pl-was-s lz-n">{sgd(d.was)}</s> : null}
                <small>{d.n} 件</small>
              </div>
              {d.note && <div className="pl-deal-how">{d.note}</div>}
              <div className="pl-deal-tags">
                {d.state === "soon" && d.starts ? (
                  <Pill tone="warn" icon="calendar">
                    {sgDay(d.starts)} 开始
                  </Pill>
                ) : d.ends ? (
                  <Pill icon="clock">
                    到 {sgDay(d.ends)} · 还有 {Math.max(0, sgDaysLeft(d.ends))} 天
                  </Pill>
                ) : (
                  <Pill tone="good">进行中</Pill>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="pl-nopromo">
          <IconBox icon="tag" />
          <div>
            <b>{p.name} 新加坡现在没有活动</b>
            <div className="pl-small">照上面的价钱报</div>
          </div>
        </div>
      )}
    </Card>
  );
}

function SGCopyCard({ sheet, p }: { sheet: PriceSheet; p: PriceProduct }) {
  const text = useMemo(() => sgCopyText(sheet, p), [sheet, p]);
  const [done, go] = useCopied(text);
  return (
    <Card className="pl-copy lz-s5">
      <SectionHead title="复制给新加坡客户" />
      {text ? (
        <>
          <Btn kind={done ? "soft" : "primary"} size="lg" icon={done ? "check" : "copy"} onClick={go}>
            {done ? "已复制" : "复制给新加坡客户"}
          </Btn>
          <Bubble text={text} note="新加坡客户收到的就是这段" />
        </>
      ) : (
        <div className="pl-nopromo">
          <IconBox icon="globe" />
          <div>
            <b>新加坡还没有价钱，没有可以复制的</b>
          </div>
        </div>
      )}
    </Card>
  );
}

/** When the list was last changed, by whom: "10/7 weijian". */
function sgStamp(sg?: PriceSG): string {
  if (!sg?.updated_at) return "";
  const d = new Date(sg.updated_at);
  const day = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kuala_Lumpur", month: "numeric", day: "numeric" }).format(d);
  return `${day} ${(sg.updated_by ?? "").split("@")[0]}`.trim();
}

/** A Singapore price as the editor holds it: whole dollars and cents typed, "" for none. */
const toCents = (v: string): number | null => {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : NaN;
};
const toDollars = (c?: number) => (c ? String(c / 100) : "");

interface EditOffer {
  n: string;
  price: string;
  was: string;
}
interface EditDeal extends EditOffer {
  label: string;
  note: string;
  starts: string;
  ends: string;
}

/** "10月31日" from "2026-10-31", with the year only when it is not this one. */
function dateSay(v: string): string {
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return v;
  const now = Number(new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuala_Lumpur", year: "numeric" }).format(new Date()));
  return `${y === now ? "" : `${y}年`}${m}月${d}日`;
}

/**
 * A date in words (10月31日, or 不限), not the browser's 10/01/2026 that follows the computer's language (QA G4-18).
 * The browser's own picker lies over it, unseen, so a press anywhere opens it (prototype core.js DateIn).
 */
function DateIn({ value, onPick, label, none = "选日期", clear }: { value: string; onPick: (v: string) => void; label: string; none?: string; clear?: boolean }) {
  return (
    <span className={cx("pl-date", !value && "none")}>
      <span className="pl-date-f">
        <ShellIcon name="calendar" size={15} />
        <span className="lz-n">{value ? dateSay(value) : none}</span>
      </span>
      <input type="date" value={value} aria-label={label} onChange={(e) => onPick(e.target.value)} />
      {clear && value && (
        <button
          type="button"
          className="pl-date-x"
          aria-label="不限日期"
          title="不限日期"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPick("");
          }}
        >
          <ShellIcon name="x" size={13} />
        </button>
      )}
    </span>
  );
}

/** 改新加坡价钱: one series at a time, the one being looked at; a panel from the right. */
function SGEditor({ sheet, p, onClose }: { sheet: PriceSheet; p: PriceProduct; onClose: () => void }) {
  const series = seriesOf(sheet, p);
  const cur = sheet.sg?.series[p.series];
  const [offers, setOffers] = useState<EditOffer[]>(() =>
    cur?.offers.length
      ? cur.offers.map((o) => ({ n: String(o.n), price: toDollars(o.cents), was: toDollars(o.was) }))
      : [
          { n: "1", price: "", was: "" },
          { n: "3", price: "", was: "" },
        ],
  );
  const [deals, setDeals] = useState<EditDeal[]>(() =>
    (cur?.deals ?? [])
      .filter((d) => d.state !== "ended")
      .map((d) => ({ n: String(d.n), price: toDollars(d.cents), was: toDollars(d.was), label: d.label, note: d.note ?? "", starts: d.starts ?? "", ends: d.ends ?? "" })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const together = sheet.products.filter((x) => x.series === p.series).length;

  const setOffer = (i: number, k: keyof EditOffer, v: string) => {
    setErr("");
    setOffers((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  };
  const setDeal = (i: number, k: keyof EditDeal, v: string) => {
    setErr("");
    setDeals((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  };

  async function save() {
    setErr("");
    const out: PriceSGSeries = { offers: [], deals: [] };
    for (const o of offers) {
      const cents = toCents(o.price);
      if (cents === null && !o.was.trim()) continue; // a row left empty is no price
      const was = toCents(o.was);
      if (!cents || Number.isNaN(was) || !(Number(o.n) > 0)) return setErr(`${o.n || "?"} ${p.unit}：件数和价钱要是数字`);
      if (out.offers.some((x) => x.n === Number(o.n))) return setErr(`${o.n} ${p.unit}写了两次`);
      out.offers.push({ n: Number(o.n), cents, ...(was ? { was } : {}) });
    }
    for (const d of deals) {
      const cents = toCents(d.price);
      const was = toCents(d.was);
      if (!d.label.trim()) return setErr("活动要有名字，例如 10 月促销");
      if (!cents || Number.isNaN(was) || !(Number(d.n) > 0)) return setErr(`活动「${d.label.trim()}」：件数和价钱要是数字`);
      if (d.starts && d.ends && d.ends < d.starts) return setErr(`活动「${d.label.trim()}」：结束在开始之前`);
      out.deals!.push({
        n: Number(d.n),
        cents,
        ...(was ? { was } : {}),
        label: d.label.trim(),
        ...(d.note.trim() ? { note: d.note.trim() } : {}),
        ...(d.starts ? { starts: d.starts } : {}),
        ...(d.ends ? { ends: d.ends } : {}),
      });
    }
    out.offers.sort((a, b) => a.n - b.n);
    const all: PriceSG = { series: { ...(sheet.sg?.series ?? {}) } };
    // The ended promotions go with the save: they are over.
    for (const [k, v] of Object.entries(all.series)) all.series[k] = { ...v, deals: (v.deals ?? []).filter((d) => d.state !== "ended") };
    all.series[p.series] = out;
    setBusy(true);
    try {
      await api.savePricesSG(all);
      await loadPrices(true);
      toast("新加坡价钱改好了，小爱下一句就照这个报", "win");
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "没存上，等一下再试");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      title={`改新加坡价钱 · ${series.name}`}
      sub="新币 SGD，包邮；改了马上生效，小爱也照这个报"
      onClose={onClose}
      busy={busy}
      foot={
        <>
          <span className="pl-small pl-sge-n">{together > 1 ? `${series.name} ${together} 个产品一起改` : "存了马上生效"}</span>
          <Btn disabled={busy} onClick={onClose}>
            取消
          </Btn>
          <Btn kind="primary" icon="check" disabled={busy} onClick={() => void save()}>
            {busy ? "存着…" : "存起来"}
          </Btn>
        </>
      }
    >
      <div className="pl-sge">
        <div className="pl-sge-k">件数和价钱</div>
        <div className="pl-sge-h">
          <span>件数</span>
          <span>价钱（SGD）</span>
          <span>原价（可空）</span>
          <span />
        </div>
        {offers.map((o, i) => (
          <div key={i} className="pl-sge-r">
            <label className="pl-in">
              <input inputMode="numeric" value={o.n} onChange={(e) => setOffer(i, "n", e.target.value)} aria-label="件数" />
              <span>{p.unit}</span>
            </label>
            <label className="pl-in">
              <span>SGD</span>
              <input inputMode="decimal" value={o.price} placeholder="例如 53" onChange={(e) => setOffer(i, "price", e.target.value)} aria-label="价钱" />
            </label>
            <label className="pl-in">
              <input inputMode="decimal" value={o.was} placeholder="原价，没有就空着" onChange={(e) => setOffer(i, "was", e.target.value)} aria-label="原价" />
            </label>
            <Btn kind="ghost" size="sm" icon="x" title="删掉这一行" onClick={() => setOffers((xs) => xs.filter((_, j) => j !== i))} />
          </div>
        ))}
        <button type="button" className="pl-addrow" onClick={() => setOffers((xs) => [...xs, { n: "", price: "", was: "" }])}>
          <ShellIcon name="plus" size={15} />
          加一个件数
        </button>
        <div className="pl-sge-k">活动（例如 10 月促销）</div>
        {deals.map((d, i) => (
          <div key={i} className="pl-sge-d">
            <div className="pl-sge-r three">
              <label className="pl-in">
                <input value={d.label} placeholder="名字，例如 10 月促销" onChange={(e) => setDeal(i, "label", e.target.value)} aria-label="活动名字" />
              </label>
              <label className="pl-in">
                <input inputMode="numeric" value={d.n} placeholder="件数" onChange={(e) => setDeal(i, "n", e.target.value)} aria-label="件数" />
                <span>{p.unit}</span>
              </label>
              <label className="pl-in">
                <span>SGD</span>
                <input inputMode="decimal" value={d.price} placeholder="价钱" onChange={(e) => setDeal(i, "price", e.target.value)} aria-label="价钱" />
              </label>
              <Btn kind="ghost" size="sm" icon="x" title="删掉这个活动" onClick={() => setDeals((xs) => xs.filter((_, j) => j !== i))} />
            </div>
            <div className="pl-sge-r dates">
              <label className="pl-sge-f">
                <small>原价</small>
                <span className="pl-in">
                  <input inputMode="decimal" value={d.was} placeholder="可空" onChange={(e) => setDeal(i, "was", e.target.value)} />
                </span>
              </label>
              <label className="pl-sge-f">
                <small>开始</small>
                <DateIn value={d.starts} label="开始" onPick={(v) => setDeal(i, "starts", v)} />
              </label>
              <label className="pl-sge-f">
                <small>到（含这一天）</small>
                <DateIn value={d.ends} label="到" none="不限" clear onPick={(v) => setDeal(i, "ends", v)} />
              </label>
            </div>
            <label className="pl-in">
              <input value={d.note} placeholder="说明（可空），例如 任选 2 盒，同款或两款各一盒" onChange={(e) => setDeal(i, "note", e.target.value)} aria-label="说明" />
            </label>
          </div>
        ))}
        <button type="button" className="pl-addrow" onClick={() => setDeals((xs) => [...xs, { n: "2", price: "", was: "", label: "", note: "", starts: "", ends: "" }])}>
          <ShellIcon name="plus" size={15} />
          加一个活动
        </button>
        {err && (
          <div className="pl-sgerr" role="alert">
            <ShellIcon name="alert" size={15} />
            {err}
          </div>
        )}
      </div>
    </Sheet>
  );
}

/* ---- one product ---- */

/** The one line under a product's name: what it costs for the person asking, and the best buy — on the site the copy
 *  button reads, named, so the line and what is copied never disagree (the table beside it stars each site's own). */
function Verdict({ sheet, p, role, market, platform }: { sheet: PriceSheet; p: PriceProduct; role: PriceRole; market: Market; platform: Platform }): ReactNode {
  const u = p.unit;
  if (market === "sg") {
    const s = sgOf(sheet, p);
    if (!s) return <span className="pl-dim">新加坡还没有价钱</span>;
    const rows = sgRows(s);
    const one = rows[0]?.offer;
    const best = bestOf(rows.map((r) => ({ n: r.n, sen: sgFirst(r) })));
    const br = rows.find((r) => r.n === best);
    return (
      <>
        {one ? (
          <>
            {rows[0]!.n} {u} <b className="lz-n">{sgd(one.cents)}</b> ·{" "}
          </>
        ) : null}
        包邮
        {best && br ? (
          <>
            {" · "}
            {best} {u}最划算，每{u} <b className="lz-n win">{perSGD(sgFirst(br)!, best)}</b>
          </>
        ) : null}
      </>
    );
  }
  const rows = rowsFor(p, role);
  if (p.addon) {
    const o = rows[0];
    const sen = o?.store[0]?.sen ?? o?.shop[0]?.sen;
    return (
      <>
        只能加购{sen != null ? <> · <b className="lz-n">{rm(sen)}</b></> : null}，要跟产品一起买
      </>
    );
  }
  if (isDealer(role)) {
    const lad = (p.on_shop && seriesOf(sheet, p).ladders?.[role]) || [];
    if (lad.length) {
      const last = lad[lad.length - 1]!;
      return (
        <>
          {ROLE_NAME[role]} · 一开始每{u} <b className="lz-n">{rm(lad[0]!.unit)}</b>
          {lad.length > 1 ? (
            <>
              {" · "}累积 {rm(last.from)} 起每{u} <b className="lz-n win">{rm(last.unit)}</b>
            </>
          ) : null}
        </>
      );
    }
    const d = dealsFor(p, role).find((x) => x.live);
    return (
      <>
        {ROLE_NAME[role]} · 没有拿货价，只卖活动{d ? <> · <b className="lz-n">{rm(d.sen)}</b> 起</> : null}
      </>
    );
  }
  // The prototype's line, 「1 盒 RM136 · 6 盒最划算，每盒 RM87.50」, read from the site the copy button sends, so the two
  // never disagree; the table beside it names both sites.
  const col = platform;
  const one = rows.find((r) => r.n === 1);
  const oneP = one?.[col][0];
  const best = bestIn(rows, col);
  const br = rows.find((r) => r.n === best);
  return (
    <>
      {role === "vip" ? "VIP · " : ""}
      {oneP ? (
        <>
          1 {u} <b className="lz-n">{rm(oneP.sen)}</b>
        </>
      ) : null}
      {best && br?.[col][0] ? (
        <>
          {oneP ? " · " : ""}
          {best} {u}最划算，每{u} <b className="lz-n win">{perRM(br[col][0].sen, best)}</b>
        </>
      ) : null}
    </>
  );
}

/** One product's prices for one person asking: the page's right side and the drawer. On the page, who is asking is
 *  picked in the product's card under its line, as the prototype's (prices.js 497); the drawer picks it above. */
export function PriceDetail({
  sheet,
  p,
  role,
  onBack,
  market = "my",
  onEditSG,
  onRole,
}: {
  sheet: PriceSheet;
  p: PriceProduct;
  role: PriceRole;
  onBack?: () => void;
  market?: Market;
  onEditSG?: () => void;
  onRole?: (r: PriceRole) => void;
}) {
  const s = seriesOf(sheet, p);
  const sg = market === "sg";
  const dealer = isDealer(role) && !p.addon;
  const sub = p.addon ? "只能加购" : s.name !== p.name ? (s.mix ? `${s.name}系列` : s.name) : (s.sub ?? "");
  const group = groupOf(sheet, p);
  const ends = !sg && !isDealer(role) && hasDeal(p, role) ? dealEnds(p, role) : "";
  const sgs = sgOf(sheet, p);
  const stamp = sgStamp(sheet.sg);
  const tkey = `${p.key}-${role}-${market}`;
  // Which site the copy button sends and the line under the name reads: store for a customer, else shop.
  const [plat, setPlat] = useState<Platform>(role === "customer" ? "store" : "shop");
  useEffect(() => setPlat(role === "customer" ? "store" : "shop"), [role]);
  const platform = platformOf(p, role, plat);

  const table = sg ? (
    <Card className="pl-table lz-s7">
      <SectionHead
        title="新加坡价钱"
        sub="新币 SGD · 包邮 · 同事用 EasyParcel 顺丰寄"
        right={
          onEditSG && sgs ? (
            <Btn size="sm" kind="soft" icon="pencil" onClick={onEditSG}>
              改新加坡价钱
            </Btn>
          ) : undefined
        }
      />
      {sgs ? (
        <SGTable key={tkey} p={p} s={sgs} />
      ) : (
        <div className="pl-nopromo">
          <IconBox icon="globe" />
          <div>
            <b>新加坡还没有价钱</b>
            {onEditSG && (
              <div className="pl-nop-a">
                <Btn size="sm" kind="soft" icon="plus" onClick={onEditSG}>
                  填新加坡价钱
                </Btn>
              </div>
            )}
          </div>
        </div>
      )}
      <SGNotes sheet={sheet} p={p} s={sgs} />
    </Card>
  ) : (
    <Card className="pl-table lz-s7">
      <SectionHead
        title={dealer ? ((p.on_shop && s.ladders?.[role]?.length) ? "累积销售额" : `${ROLE_NAME[role]} 价`) : role === "vip" ? "VIP 买几件收多少" : "买几件收多少"}
        sub={
          dealer
            ? (p.on_shop && s.ladders?.[role]?.length)
              ? `${ROLE_NAME[role]} · 卖得越多，每${p.unit}拿货价越低`
              : "没有拿货价，照下面的活动报"
            : role === "vip"
              ? "VIP 只在 shop.lazior.com 有自己的价"
              : "两个网站，价钱不一样时看清楚"
        }
      />
      {dealer ? <Ladder key={tkey} sheet={sheet} p={p} role={role} /> : <CompareTable key={tkey} p={p} role={role} />}
      <Notes sheet={sheet} p={p} role={role} />
    </Card>
  );

  return (
    <div className={cx("pl-detail", sg && "sg")}>
      <Card className="pl-hero">
        {onBack && (
          <div className="pl-back">
            <Btn kind="ghost" size="sm" icon="left" onClick={onBack}>
              全部产品
            </Btn>
          </div>
        )}
        <div className="pl-stage">
          {p.image ? <img key={p.key} src={p.image} alt={p.name} decoding="async" /> : <i>{p.name.slice(0, 1)}</i>}
        </div>
        <div className="pl-hero-m">
          <div className="pl-series">
            {sub}
            {sub && group ? " · " : ""}
            {group}
          </div>
          <h2>
            <span>{p.name}</span>
            {!sg && p.on_store && !p.available && (
              <Pill tone="bad" icon="alert">
                store 缺货
              </Pill>
            )}
            {ends && (
              <Pill tone="warn" icon="clock">
                活动到 {ends}
              </Pill>
            )}
          </h2>
          <div className="pl-verdict" key={tkey}>
            <Verdict sheet={sheet} p={p} role={role} market={market} platform={platform} />
          </div>
          {/* Under a line, who is asking — or, in Singapore, which list this is (prototype prices.js 497) */}
          {(sg || onRole) && (
            <div className="pl-ask">
              {sg ? (
                <span className="pl-sgtag">
                  <i className="pl-sdot sg" />
                  新加坡 · 新币 SGD · 包邮
                </span>
              ) : (
                <>
                  <span className="pl-small">谁在问</span>
                  <RoleSeg role={role} onRole={onRole!} />
                </>
              )}
            </div>
          )}
        </div>
      </Card>
      <div className="lz-grid pl-grid">
        {table}
        {sg ? (
          <SGCopyCard sheet={sheet} p={p} />
        ) : (
          <CopyCard sheet={sheet} p={p} role={role} platform={platform} onPick={setPlat} />
        )}
        {sg ? <SGPromos p={p} s={sgs} /> : <Promos p={p} role={role} />}
      </div>
      {sg ? (
        <div className="pl-foot">
          <span>
            <i className="pl-sdot sg" />
            新加坡 · 团队自己填的价钱{stamp ? ` · ${stamp} 改` : ""}
          </span>
          {onEditSG && sgs && (
            <button type="button" className="pl-link" onClick={onEditSG}>
              <ShellIcon name="pencil" size={13} />
              改新加坡价钱
            </button>
          )}
        </div>
      ) : (
        <Sources sheet={sheet} />
      )}
    </div>
  );
}

/* ---- the list ---- */

/** One unit's price beside a product: the storefront's, and shop.lazior.com's under it when it differs. */
function Headline({ sheet, p, role, market = "my" }: { sheet: PriceSheet; p: PriceProduct; role: PriceRole; market?: Market }) {
  if (market === "sg") {
    const c = sgHeadline(sheet, p);
    return (
      <span className="pl-ip">
        {c != null ? (
          <b className="lz-n">
            {sgLive(sgOf(sheet, p)).length > 0 && <i className="pl-dot" title="有活动" />}
            {sgd(c)}
          </b>
        ) : (
          <span className="pl-small">—</span>
        )}
      </span>
    );
  }
  const h = headline(sheet, p, role);
  const dot = hasDeal(p, role) && <i className="pl-dot" title="有活动" />;
  if (h.range) {
    return (
      <span className="pl-ip">
        <b className="lz-n">
          {dot}
          {h.range}
        </b>
        <small>{ROLE_NAME[role]}</small>
      </span>
    );
  }
  if (h.shop != null && h.store != null && h.shop !== h.store) {
    return (
      <span className="pl-ip" title="store.lazior.com / shop.lazior.com">
        <b className="lz-n store">
          {dot}
          {rm(h.store)}
        </b>
        <small className="lz-n">shop {rm(h.shop)}</small>
      </span>
    );
  }
  // Only one shop has a price for this person: say which, so it is not read as both.
  const one = h.store ?? h.shop;
  const only = h.shop == null && one != null && p.on_shop ? "只在 store" : h.store == null && one != null && p.on_store ? "只在 shop" : "";
  return (
    <span className="pl-ip">
      <b className="lz-n">
        {dot}
        {one != null ? rm(one) : ""}
      </b>
      {only && <small>{only}</small>}
    </span>
  );
}

/** The products matching what was typed, under their headings. */
function useMatches(sheet: PriceSheet | null, q: string) {
  return useMemo(() => {
    if (!sheet) return [];
    const by = new Map(sheet.products.map((p) => [p.key, p]));
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return sheet.groups
      .map((g) => ({
        name: g.name,
        products: g.products
          .map((k) => by.get(k))
          .filter((p): p is PriceProduct => !!p && words.every((w) => haystack(sheet, p).includes(w))),
      }))
      .filter((g) => g.products.length > 0);
  }, [sheet, q]);
}

/** What the list says under a product's name: its series, or what it is. */
function rowSub(sheet: PriceSheet, p: PriceProduct): string {
  const s = seriesOf(sheet, p);
  if (p.addon) return "只能加购";
  if (s.name !== p.name) return s.name;
  return s.sub ?? "";
}

function Thumb({ p }: { p: PriceProduct }) {
  return <span className="pl-thumb">{p.image ? <img src={p.image} alt="" loading="lazy" decoding="async" /> : <i>{p.name.slice(0, 1)}</i>}</span>;
}

function Find({ q, onQ, small }: { q: string; onQ: (q: string) => void; small?: boolean }) {
  return (
    <label className={cx("pl-find", small && "sm")}>
      <ShellIcon name="search" size={17} />
      <input
        type="search"
        value={q}
        onChange={(e) => onQ(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && onQ("")}
        placeholder={small ? "找产品" : "找产品：kiwi、抹茶、zenite…"}
        aria-label="找产品"
      />
      {q && (
        <button type="button" className="pl-clear" aria-label="清掉" title="清掉" onClick={() => onQ("")}>
          <ShellIcon name="x" size={15} />
        </button>
      )}
    </label>
  );
}

function PricesView({ active, mayEditSG }: { active: boolean; mayEditSG?: boolean }) {
  const { sheet, error } = usePrices(active);
  const [role, setRole] = useRole();
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  // 马来西亚 or 新加坡 for the whole page. Not remembered: the page opens on
  // Malaysia, where nearly every customer is, and a Singapore chat's 价钱
  // opens on Singapore by itself.
  const [market, setMarket] = useState<Market>("my");
  const [editing, setEditing] = useState(false);
  // On a phone the list and a product are one screen each; back closes the product.
  const [open, setOpen] = useState(false);
  useBackLayer(open, () => setOpen(false));
  // A phone scrolls the page itself: a product opens at its top, and back
  // returns to where the list was left.
  const scroller = useRef<HTMLDivElement>(null);
  // Coming on screen (kit usePlay): the cards rise and the price rows come in one after another (prices.css).
  const [playRef, play] = usePlay<HTMLDivElement>();
  const pageRef = useCallback(
    (el: HTMLDivElement | null) => {
      scroller.current = el;
      playRef(el);
    },
    [playRef],
  );
  const listAt = useRef(0);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !window.matchMedia("(max-width: 800px)").matches) return;
    el.scrollTop = open ? 0 : listAt.current;
  }, [open]);
  const groups = useMatches(sheet, q);
  const prod = sheet?.products.find((p) => p.key === pick) ?? groups[0]?.products[0] ?? sheet?.products[0];
  const ends = useMemo(() => (sheet ? sheetEnds(sheet) : null), [sheet]);

  if (!sheet) {
    if (!error) return <PageSkel page="prices" />;
    return (
      <div className="lz-page">
        <div className="lz pl">
          <PageHead title="价目表" />
          <Card className="pl-error">
            <IconBox icon="alert" tone="warn" />
            <b>{error}</b>
          </Card>
        </div>
      </div>
    );
  }
  const answer =
    market === "sg" ? (
      <>新加坡 · 新币 SGD，包邮 · {mayEditSG ? "价钱在这里改，马上生效" : "团队自己填的价钱"}</>
    ) : ends ? (
      <>
        {ends.all ? "活动到 " : "最近一个活动 "}
        <b>{ends.day}</b>
        {ends.all ? "" : " 结束"}，还有 <b className="lz-n">{ends.left} 天</b> · 按谁在问报价，复制就能发
      </>
    ) : (
      "按谁在问报价，复制就能发"
    );
  return (
    <div className="lz-page pl-lock" ref={pageRef}>
      <div className={cx("lz pl", market === "sg" && "sg", play && "play")}>
        <PageHead title="价目表" answer={answer} right={<MarketSeg market={market} onMarket={setMarket} />} />
        <div className="pl-split" data-view={open ? "detail" : "list"}>
          <Card className="pl-list" pad={0}>
            <div className="pl-search">
              <Find q={q} onQ={setQ} />
            </div>
            <div className="pl-scroll">
              {groups.map((g) => (
                <div key={g.name} className="pl-group">
                  <div className="pl-g">{g.name}</div>
                  {g.products.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      className={cx("pl-item", prod?.key === p.key && "on", market === "sg" && !sgOf(sheet, p) && "dim")}
                      aria-current={prod?.key === p.key ? "true" : undefined}
                      onClick={() => {
                        if (scroller.current) listAt.current = scroller.current.scrollTop;
                        setPick(p.key);
                        setOpen(true);
                      }}
                    >
                      <Thumb p={p} />
                      <span className="pl-it">
                        <b>{p.name}</b>
                        <span>
                          {rowSub(sheet, p)}
                          {market === "my" && p.on_store && !p.available && <i className="pl-out">缺货</i>}
                        </span>
                      </span>
                      <Headline sheet={sheet} p={p} role={role} market={market} />
                    </button>
                  ))}
                </div>
              ))}
              {groups.length === 0 && (
                <Empty
                  icon="search"
                  line={`没有「${q}」`}
                  action={
                    <Btn size="sm" onClick={() => setQ("")}>
                      看全部产品
                    </Btn>
                  }
                />
              )}
              {error && <p className="pl-stale">{error}</p>}
            </div>
          </Card>
          <div className="pl-side">
            {prod && (
              <PriceDetail
                sheet={sheet}
                p={prod}
                role={role}
                onBack={() => setOpen(false)}
                market={market}
                // The shaker too: it has a Singapore add-on price since 2026-10-28 (owner: 「shaker新加坡SGD 5加购价」),
                // and store.lazior.com charges a parcel to Singapore by this list.
                onEditSG={mayEditSG ? () => setEditing(true) : undefined}
                onRole={setRole}
              />
            )}
          </div>
        </div>
      </div>
      {editing && prod && <SGEditor sheet={sheet} p={prod} onClose={() => setEditing(false)} />}
    </div>
  );
}

export default memo(PricesView);

/** The first product of the series a conversation's product names: its key, or one of the series' words. */
function productFor(sheet: PriceSheet, product?: string): PriceProduct | undefined {
  const want = (product ?? "").trim().toLowerCase();
  if (!want) return undefined;
  return (
    sheet.products.find((p) => p.series === want || p.key === want) ??
    sheet.products.find((p) => p.words?.includes(want) || seriesOf(sheet, p).name.toLowerCase().includes(want))
  );
}

/** One product in the chat's 价钱 drawer, as the prototype's card (chat.js ChatPrices): its picture, name and a pill, the
 *  prices for whoever is asking, the notes. With `onPick` a price pressed goes into the reply box. */
function DrawerProduct({ sheet, p, role, market, asked, onPick }: { sheet: PriceSheet; p: PriceProduct; role: PriceRole; market: Market; asked: boolean; onPick?: (line: string) => void }) {
  const s = seriesOf(sheet, p);
  const sg = market === "sg";
  const sgs = sgOf(sheet, p);
  const dealer = isDealer(role) && !p.addon;
  const sub = p.addon ? "只能加购" : s.name !== p.name ? s.name : (s.sub ?? "");
  const hot = sg ? sgLive(sgs).length > 0 : hasDeal(p, role);
  const deals = dealer ? dealsFor(p, role).filter((d) => d.live) : [];
  return (
    <section className="pl-pd-card" key={`${p.key}-${role}-${market}`}>
      <div className="pl-pd-h">
        <span className="pl-pd-img">{p.image ? <img src={p.image} alt="" decoding="async" /> : <i>{p.name.slice(0, 1)}</i>}</span>
        <span className="pl-pd-ht">
          <b>{p.name}</b>
          <small>
            {sub}
            {asked && (
              <>
                {sub ? " · " : ""}
                <span className="pl-pd-ask">客户在问这个</span>
              </>
            )}
          </small>
        </span>
        {!sg && p.on_store && !p.available ? (
          <Pill tone="bad" icon="alert">
            缺货
          </Pill>
        ) : hot ? (
          <Pill tone="warn" icon="clock">
            有活动
          </Pill>
        ) : null}
      </div>
      {sg ? (
        sgs ? (
          <SGTable p={p} s={sgs} />
        ) : (
          <p className="pl-pd-why">新加坡还没有价钱</p>
        )
      ) : dealer ? (
        <>
          <Ladder sheet={sheet} p={p} role={role} />
          {deals.length > 0 && (
            <div className="pl-pd-deals">
              {deals.map((d, i) => {
                const line = `${p.name} ${d.label || d.name}：${d.units} ${p.unit} ${rm(d.sen)}`;
                const body = (
                  <>
                    <b>{d.label || d.name}</b>
                    <span className="lz-n">
                      {d.units} {p.unit} {rm(d.sen)}
                    </span>
                    <small>
                      <DealBits d={d} unit={p.unit} />
                    </small>
                    {onPick && <ShellIcon name="plus" size={14} className="pl-pd-add" />}
                  </>
                );
                return onPick ? (
                  <button key={i} type="button" className="pl-pd-deal" title="放进输入框" onClick={() => onPick(line)}>
                    {body}
                  </button>
                ) : (
                  <div key={i} className="pl-pd-deal">
                    {body}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <CompareTable p={p} role={role} onPick={onPick} />
      )}
      {sg ? <SGNotes sheet={sheet} p={p} s={sgs} /> : <Notes sheet={sheet} p={p} role={role} />}
    </section>
  );
}

/** The words to paste under the product, as the prototype's 「给客户的一段」: the site to read, the bubble, and the buttons. */
function DrawerCopy({ sheet, p, role, market, onPick }: { sheet: PriceSheet; p: PriceProduct; role: PriceRole; market: Market; onPick?: (text: string) => void }) {
  const sg = market === "sg";
  const dealer = isDealer(role);
  const [plat, setPlat] = useState<Platform>(role === "customer" ? "store" : "shop");
  useEffect(() => setPlat(role === "customer" ? "store" : "shop"), [role]);
  const platform = platformOf(p, role, plat);
  const full = useMemo(() => (sg ? sgCopyText(sheet, p) : copyText(sheet, p, role, platform)), [sg, sheet, p, role, platform]);
  // A dealer's long ladder goes short; the whole one is the quieter button.
  const short = useMemo(() => (sg ? null : shortLadder(sheet, p, role)), [sg, sheet, p, role]);
  const text = short ?? full;
  const [done, go] = useCopied(text);
  const [doneAll, goAll] = useCopied(full);
  if (!text) return null;
  const who = sg ? "新加坡客户" : role === "distributor" ? "经销商" : dealer && !p.addon ? "代理" : role === "vip" ? " VIP" : "客户";
  const choose = !sg && p.on_store && p.on_shop && (!dealer || p.addon);
  return (
    <div className="pl-pd-copy">
      <div className="pl-pd-ch">
        <b>给{who}的一段</b>
        {choose && (
          <Seg
            size="sm"
            value={platform}
            onChange={setPlat}
            options={[
              ["store", "store"],
              ["shop", "shop"],
            ]}
          />
        )}
      </div>
      <div className="pl-pd-bubble" key={text}>
        {text}
      </div>
      <div className="pl-pd-acts">
        {onPick && (
          <Btn kind="primary" icon="send" onClick={() => onPick(text)}>
            整段放进输入框
          </Btn>
        )}
        <Btn kind={onPick ? "ghost" : done ? "soft" : "primary"} icon={done ? "check" : "copy"} onClick={go}>
          {done ? "已复制" : onPick ? "复制" : `复制给${who}`}
        </Btn>
        {short && (
          <Btn kind="ghost" icon={doneAll ? "check" : "copy"} onClick={goAll}>
            {doneAll ? "已复制" : "复制整张阶梯"}
          </Btn>
        )}
      </div>
    </div>
  );
}

/** The chat's 价钱 drawer: the same answer beside the conversation, the chat's product first. `onPick`, when the chat
 *  gives it, puts a price or the whole of the words into its reply box (prototype chat.js ChatPrices). */
export function PriceDrawer({ product, singapore, onClose, onPick }: { product?: string; singapore?: boolean; onClose: () => void; onPick?: (text: string) => void }) {
  const { sheet, error } = usePrices(true);
  const [role, setRole] = useRole();
  const [pick, setPick] = useState("");
  const [q, setQ] = useState("");
  const [market, setMarket] = useState<Market>(singapore ? "sg" : "my");
  const groups = useMatches(sheet, q);
  const asked = sheet ? productFor(sheet, product) : undefined;
  const prod = sheet ? (sheet.products.find((p) => p.key === pick) ?? asked ?? sheet.products[0]) : undefined;
  // The chosen product's chip in view: the chat's product may be far along the row.
  const chips = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = chips.current;
    const on = row?.querySelector<HTMLElement>("button.on");
    // The picked one in the middle, its neighbours half shown under the fades: at the left edge the chip before it
    // was cut to 「茶谷粮」.
    if (row && on) row.scrollTo({ left: Math.max(0, on.offsetLeft - row.offsetLeft - (row.clientWidth - on.offsetWidth) / 2) });
    if (row) fade(row);
  }, [prod?.key, sheet]);
  // The row fades out only on a side with more chips past it (prototype chat.js ChatPrices f-l / f-r / f-lr).
  const [edge, setEdge] = useState("");
  const fade = (row: HTMLDivElement) => {
    const l = row.scrollLeft > 4;
    const r = row.scrollLeft + row.clientWidth < row.scrollWidth - 4;
    setEdge(l && r ? "f-lr" : l ? "f-l" : r ? "f-r" : "");
  };
  return (
    <div className="wc-drawer pl-drawer" role="dialog" aria-label="价钱">
      <div className="wc-drawer-h pl-dh">
        <span className="pl-dh-t">
          <b>价钱</b>
          <span>{market === "sg" ? "新加坡 · 新币 SGD · 包邮" : onPick ? "按谁在问报价 · 按价钱就放进输入框" : "按谁在问报价 · 复制就能发"}</span>
        </span>
        <button type="button" className="wc-ico" aria-label="关上" onClick={onClose}>
          <ShellIcon name="x" size={20} />
        </button>
      </div>
      <div className="wc-drawer-b pl">
        {!sheet && (
          <p className="pl-wait">
            {!error && <i className="pl-spin" aria-hidden="true" />}
            {error || "正在读取价目表…"}
          </p>
        )}
        {sheet && (
          <>
            <div className="pl-pd-top">
              <MarketSeg market={market} onMarket={setMarket} />
              {singapore && (
                <Pill tone="msg" icon="globe">
                  新加坡号码
                </Pill>
              )}
            </div>
            <div className="pl-pd-who">
              <span>谁在问</span>
              {market === "my" ? <RoleSeg role={role} onRole={setRole} /> : <span className="pl-small">新加坡只有客户价，包邮</span>}
            </div>
            <Find q={q} onQ={setQ} />
            <div className={cx("pl-chips", edge)} role="tablist" aria-label="产品" ref={chips} onScroll={(e) => fade(e.currentTarget)}>
              {groups.flatMap((g) => g.products).map((p) => (
                <button key={p.key} type="button" role="tab" aria-selected={prod?.key === p.key} className={prod?.key === p.key ? "on" : ""} onClick={() => setPick(p.key)}>
                  {p.image ? <img src={p.image} alt="" loading="lazy" decoding="async" /> : null}
                  {p.name}
                </button>
              ))}
              {groups.length === 0 && <span className="pl-small">没有「{q}」</span>}
            </div>
            {prod && <DrawerProduct sheet={sheet} p={prod} role={role} market={market} asked={prod.key === asked?.key} onPick={onPick} />}
            {prod && <DrawerCopy sheet={sheet} p={prod} role={role} market={market} onPick={onPick} />}
            <Btn
              kind="ghost"
              onClick={() => {
                onClose();
                window.location.hash = "#prices";
              }}
            >
              完整价目表
              <ShellIcon name="right" size={16} />
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}
